import { cva, type VariantProps } from 'class-variance-authority';
import { CircleAlert, CircleCheck, Info, Lightbulb, TriangleAlert } from 'lucide-react';
import type { ComponentPropsWithRef, ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

const alertVariants = cva('flex gap-3 rounded-xl border p-4 text-sm', {
  variants: {
    tone: {
      info: 'border-info-100 bg-info-50 text-fg',
      success: 'border-success-100 bg-success-50 text-fg',
      warning: 'border-warning-100 bg-warning-50 text-fg',
      danger: 'border-danger-100 bg-danger-50 text-fg',
      /** Podpowiedz w tonacji marki — odpowiednik pola „WSKAZÓWKA” z makiet. */
      tip: 'border-brand-200 bg-brand-subtle text-fg',
    },
  },
  defaultVariants: {
    tone: 'info',
  },
});

const toneIcons = {
  info: Info,
  success: CircleCheck,
  warning: TriangleAlert,
  danger: CircleAlert,
  tip: Lightbulb,
} as const;

const iconColors = {
  info: 'text-info-500',
  success: 'text-success-500',
  warning: 'text-warning-500',
  danger: 'text-danger-500',
  tip: 'text-brand-700',
} as const;

export interface AlertProps
  extends Omit<ComponentPropsWithRef<'div'>, 'title'>,
    VariantProps<typeof alertVariants> {
  /** Naglowek komunikatu. W makietach pisany wersalikami (np. „WAŻNE”). */
  title?: ReactNode;
  /** Wlasna ikona zamiast domyslnej dla danego tonu. */
  icon?: ReactNode;
  /** Ukrywa ikone calkowicie. */
  hideIcon?: boolean;
}

/** Komunikat kontekstowy — informacja, ostrzezenie, blad lub podpowiedz. */
export function Alert({
  className,
  tone = 'info',
  title,
  icon,
  hideIcon = false,
  children,
  ...props
}: AlertProps) {
  const ToneIcon = toneIcons[tone ?? 'info'];

  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn(alertVariants({ tone }), className)}
      {...props}
    >
      {!hideIcon && (
        <span className={cn('mt-px shrink-0 [&_svg]:size-[1.125rem]', iconColors[tone ?? 'info'])}>
          {icon ?? <ToneIcon aria-hidden="true" />}
        </span>
      )}

      <div className="flex min-w-0 flex-col gap-1">
        {title && (
          <p className="text-fg-strong text-xs font-bold tracking-[0.06em] uppercase">{title}</p>
        )}
        {children && <div className="text-fg-muted leading-relaxed">{children}</div>}
      </div>
    </div>
  );
}
